import { Model } from 'app/model/backendmodel';
import { convertObjectToString, format } from 'app/shared/utils/string.util';
import { IValidationContext, IntegrationRuntime, ManagedIntegrationRuntime } from 'shell-models-lib';
import { IntegrationRuntimeAdapters } from '../integrationruntime.adapter';
import { IModelService } from 'app/model/services/IModelService';

import { irStatusConst, irTypesConst } from '../integrationruntime.constants';
import { IntegrationRuntimeOperationsService } from 'app/apis/integration-runtime-operations-api.service';
import { LoggerService } from 'app/logger/logger.service';
import { IntegrationRuntimeStatusService } from './integrationruntime-status.service';
import { GlobalVariables } from 'app/services/globals';
import { formatRegionString, isDataFlowAdbRegion, regions } from 'app/shared/utils/region.util';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';

export interface IIRSubstatus {
    substatus: string;
    errorMessage?: string;
}

function isSparkGatewayWithUnsupportedFeatureInNonAdbRegion(ir: IntegrationRuntime, factoryLocation: string, services: { exposureControlService: ExposureControlService }): boolean {
    if (__IS_A365__ ||
        !factoryLocation ||
        isDataFlowAdbRegion(factoryLocation) ||
        !services.exposureControlService.enableADFSparkGateway) {
        return false;
    }

    if (ir instanceof ManagedIntegrationRuntime) {
        const formattedComputeLocation = formatRegionString(ir.computeLocation);
        const irType = IntegrationRuntimeAdapters.GetIRTypeFromLocalModel(ir);

        return  (formattedComputeLocation !== regions.AutoResolve && factoryLocation !== formattedComputeLocation && !services.exposureControlService.enableADFSparkGatewayMultiRegion) ||
            (irType === irTypesConst.ManagedVNet && !services.exposureControlService.enableADFSparkGatewayManagedVnet) ||
            (ir.dataFlowCleanup === false && !services.exposureControlService.enableADFSparkGatewayQuickReuse) ||
            ((ir.dataFlowCleanup == null || ir.dataFlowCleanup) && ir.dataFlowTimeToLive > 0); // disable due to warm pool
    }

    return false;
}

export function getDataFlowIrSparkGatewayErrorMessage(ir: IntegrationRuntime, factoryLocation: string, services: { exposureControlService: ExposureControlService }): string {
    if (isSparkGatewayWithUnsupportedFeatureInNonAdbRegion(ir, factoryLocation, services)) {
        return stringResources.DataFlow_SparkGateway_Disabled;
    }

    return undefined;
}

export async function isDataflowIRReadyModelValidator(ir: IntegrationRuntime, context: IValidationContext): Promise<string> {
    const status = await getVNetIRDataflowStatus(ir, context);

    // If non-vnet IR then it is enabled.
    if (!isDataflowIRReady(status.substatus)) {
        return status.errorMessage;
    }

    return undefined;
}

export async function getVNetIRDataflowStatus(ir: IntegrationRuntime, context: IValidationContext): Promise<IIRSubstatus> {
    try {
        ir = await getVnetIR(ir, context);
        const exposureControlService = await getServiceInstance<ExposureControlService>(
            context.contextData,
            'ExposureControlService'
        );

        // No VNet or CmdApi scenario
        if (!ir || GlobalVariables.isCmdApiApp || exposureControlService.enableADFSparkGatewayManagedVnet) {
            return { substatus: irStatusConst.Succeeded };
        }

        // Check if the status is cached and it is a terminal state to avoid an API call.
        const irStatusService = await getServiceInstance<IntegrationRuntimeStatusService>(context.contextData, 'IntegrationRuntimeStatusService');
        const fullIrStatus = irStatusService.getCachedStatus(ir.name);
        const irCachedStatus: Model.ManagedIntegrationRuntimeStatusTypeProperties = fullIrStatus && fullIrStatus.typeProperties;
        if (irCachedStatus && isTerminalState(irCachedStatus.dataflowState)) {
            return generateResponseFromStatus(irCachedStatus, ir.name);
        }

        // Refresh IR state
        const irOperationService = await getServiceInstance<IntegrationRuntimeOperationsService>(context.contextData, 'IntegrationRuntimeOperationsService');
        const irStatus: Model.IntegrationRuntimeStatusResponse = await irOperationService.getStatusAsync(ir.name);
        const irStatusTypeProperties: Model.ManagedIntegrationRuntimeStatusTypeProperties = irStatus.properties.typeProperties;
        return generateResponseFromStatus(irStatusTypeProperties, ir.name);
    } catch (e) {
        const logger = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');
        logger.warn('getVNetIRDataflowStatus', convertObjectToString(e));

        // Allow execution if retrieving IR status fails to avoid blocking users.
        return {
            substatus: irStatusConst.Succeeded
        };
    }
}

/**
 *
 * @returns IR if VNet enabled. Otherwise undefined.
 */
export async function getVnetIR(ir: IntegrationRuntime, context: IValidationContext): Promise<IntegrationRuntime> {
    // Cannot reference model service directly as it creates a circular depedency.
    const modelService = await getServiceInstance<IModelService>(context.contextData, 'ModelService');

    if (!ir) {
        return undefined;
    }
    await modelService.fetch(ir);

    const irType = IntegrationRuntimeAdapters.GetIRTypeFromLocalModel(ir);
    return (irType === irTypesConst.ManagedVNet) ? ir : undefined;
}

function isDataflowIRReady(status: string): boolean {
    return status === irStatusConst.Succeeded;
}

function isDataflowIRFailed(status: string): boolean {
    return status === irStatusConst.Failed;
}

function isTerminalState(status: string): boolean {
    return isDataflowIRReady(status) || isDataflowIRFailed(status);
}

function generateResponseFromStatus(irStatus: Model.ManagedIntegrationRuntimeStatusTypeProperties, irName: string): IIRSubstatus {
    return  {
        substatus: irStatus.dataflowState,
        errorMessage: isDataflowIRFailed(irStatus.dataflowState)
            ? format(stringResources.DataflowStateIRStateFailedMessage, irName)
            : !isDataflowIRReady(irStatus.dataflowState)
                ? format(stringResources.DataflowStateIRStateErrorMessage, irName)
                : undefined
    };
}
